import React, { useState } from "react";
import { FaTimes, FaUserPlus, FaUser } from "react-icons/fa";

const IssueAssignModal = ({ show, issue, employees, onClose, onAssign }) => {
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [loading, setLoading] = useState(false);

  // Debug logging
  console.log("IssueAssignModal - employees prop:", employees);
  console.log("IssueAssignModal - employees length:", employees?.length);

  const handleAssign = async () => {
    if (!selectedEmployee) {
      alert("Please select an employee to assign");
      return;
    }

    setLoading(true);
    try {
      await onAssign(issue._id, selectedEmployee);
      setSelectedEmployee("");
    } catch (error) {
      console.error("Assignment error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedEmployee("");
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaUserPlus className="text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Assign Issue
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes />
          </button>
        </div>

        {/* Issue Details */}
        {issue && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-1">{issue.title}</h4>
            <p className="text-sm text-gray-600">{issue.issueType}</p>
            <p className="text-xs text-gray-500 mt-1">Phone: {issue.phone}</p>
          </div>
        )}

        {/* Employee Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Employee to Assign:
          </label>
          {employees.length > 0 ? (
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Choose an employee...</option>
              {employees.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {employee.fullName} ({employee.role?.slug || employee.role}) -{" "}
                  {employee.city}
                </option>
              ))}
            </select>
          ) : (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                No available employees found. As a senior employee, you can only
                assign issues to junior employees in your city.
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Debug: Received {employees?.length || 0} employees in props
              </p>
            </div>
          )}
        </div>

        {/* Employee List Preview */}
        {employees.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Available Employees:
            </p>
            <div className="max-h-32 overflow-y-auto">
              {employees.map((employee) => (
                <div
                  key={employee._id}
                  className="flex items-center p-2 hover:bg-gray-50 rounded"
                >
                  <FaUser className="text-gray-400 mr-2" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{employee.fullName}</p>
                    <p className="text-xs text-gray-500">
                      {employee.role?.slug || employee.role} • {employee.city} •{" "}
                      {employee.employeeCode}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedEmployee || loading || employees.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Assigning..."
              : employees.length === 0
              ? "No Employees Available"
              : "Assign Issue"}
          </button>
        </div>

        {/* Assignment Note */}
        <div className="mt-3 p-2 bg-blue-50 rounded-md">
          <p className="text-xs text-blue-600">
            <strong>Note:</strong> Once assigned, the issue status will
            automatically change to "In Progress" and the assigned employee will
            be notified.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IssueAssignModal;
